import { getCookie, setCookie } from '../../../../../../shared/infrastructure/utils/cookie-utils';
import type { IAppLoaderStorage } from '../../../../../infrastructure/types/utils/storage-types';
import {
  TrackingCookie,
  TRACKING_COOKIES_TO_CLEAR,
  REGISTRATION_START_PAGE_FALLBACK,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import type {
  RegisterMetadata,
  RegisterRequest,
  RequestPhoneVerificationMetadata,
} from '../../../../../infrastructure/types/features/auth/auth-contracts';

type TrackingRegistrationFields = Pick<
  RegisterRequest,
  'latest_request_uri' | 'referrer' | 'roistat_id' | 'metadata'
>;

class TrackingManager {
  constructor(private readonly appStorage: IAppLoaderStorage) {}

  private readRawCookie(name: string): string | undefined {
    const prefix = `${name}=`;

    return document.cookie
      .split('; ')
      .filter(entry => entry.startsWith(prefix))
      .map(entry => entry.slice(prefix.length))
      .find(value => value.length > 0);
  }

  get latestRequestUri(): LatestRequestUri | undefined {
    return (this.readRawCookie(TrackingCookie.TRACKING_URL) ??
      this.readRawCookie(TrackingCookie.LATEST_REQUEST_URI)) as LatestRequestUri | undefined;
  }

  get referrer(): TrackingReferrer | undefined {
    return (this.readRawCookie(TrackingCookie.PARTNER_REFERRER) ??
      this.readRawCookie(TrackingCookie.TRACKING_REFERRER) ??
      this.readRawCookie(TrackingCookie.REFERRER)) as TrackingReferrer | undefined;
  }

  get roistatId(): RoistatId | undefined {
    return this.readRawCookie(TrackingCookie.ROISTAT_VISIT) as RoistatId | undefined;
  }

  get registrationStartPage(): MetrikaStartPage {
    const value = getCookie(TrackingCookie.REGISTRATION_START_PAGE);
    return (value ? value : REGISTRATION_START_PAGE_FALLBACK) as MetrikaStartPage;
  }

  private get ymUid(): MetrikaUid | undefined {
    return this.readRawCookie(TrackingCookie.YM_UID) as MetrikaUid | undefined;
  }

  private get gaUid(): GoogleAnalyticsUid | undefined {
    return this.readRawCookie(TrackingCookie.GA_UID) as GoogleAnalyticsUid | undefined;
  }

  get metadata(): RegisterMetadata {
    const ymUid = this.ymUid;
    const gaUid = this.gaUid;

    const metadata: RegisterMetadata = {};
    if (ymUid) {
      metadata.ym_uid = ymUid;
    }
    if (gaUid) {
      metadata.ga_uid = gaUid;
    }
    return metadata;
  }

  get verificationMetadata(): RequestPhoneVerificationMetadata {
    const latestRequestUri = this.latestRequestUri;
    const referrer = this.referrer;
    const roistatId = this.roistatId;
    const metadata = this.metadata;

    const verificationMetadata: RequestPhoneVerificationMetadata = {};
    if (latestRequestUri) {
      verificationMetadata.latest_request_uri = latestRequestUri;
    }
    if (referrer) {
      verificationMetadata.referrer = referrer;
    }
    if (roistatId) {
      verificationMetadata.roistat_id = roistatId;
    }
    if (metadata.ym_uid || metadata.ga_uid) {
      verificationMetadata.metadata = metadata;
    }
    return verificationMetadata;
  }

  get registrationFields(): TrackingRegistrationFields {
    const latestRequestUri = this.latestRequestUri;
    const referrer = this.referrer;
    const roistatId = this.roistatId;
    const metadata = this.metadata;

    const fields: TrackingRegistrationFields = {};
    if (latestRequestUri) {
      fields.latest_request_uri = latestRequestUri;
    }
    if (referrer) {
      fields.referrer = referrer;
    }
    if (roistatId) {
      fields.roistat_id = roistatId;
    }
    if (metadata.ym_uid || metadata.ga_uid) {
      fields.metadata = metadata;
    }
    return fields;
  }

  clear() {
    TRACKING_COOKIES_TO_CLEAR.forEach(name =>
      setCookie(name, '', {
        path: '/',
        domain: ('.' + this.appStorage.domain) as Host,
        'max-age': -1,
      }),
    );
  }
}

export { TrackingManager };
